/** 240 topics for a unit (R12, KTD9). Pure: the search is the Worker's, the ranking is here. */

/** A CGraph search hit. Only `published` is ever shown. */
export interface TopicCandidate {
  id: string;
  name: string;
  displayName?: string;
  status: 'published' | 'draft' | 'archived';
  score?: number;
}

/** A topic as a unit lists it: `matched` is how many of the unit's standards found it. */
export interface UnitTopic {
  id: string;
  title: string;
  matched: number;
}

/** One block of published study material, as the study app stores it. */
export interface RichContent {
  text: string;
  images?: { id?: string; url: string; alttext?: string; name?: string }[];
  videos?: { id?: string; name?: string; url?: string }[];
  sounds?: { id?: string; name?: string; url?: string }[];
}

export interface TopicPage {
  id: string;
  name: string;
  status: 'published';
  materials: RichContent[];
  provider: 'cgraph' | 'fixture';
}

/** Topics a unit keeps (Assumptions: five is useful without crowding the view). */
export const TOPICS_PER_UNIT = 5;
/** A standard "matches" a topic only when the topic is among its first few hits. */
export const HITS_PER_STANDARD = 5;

/** A unit's searches are capped so a very large unit stays inside one request's budget. */
export const MAX_SEARCHES_PER_UNIT = 20;

/** The cache key for a unit's topics: its standard set, in any order (KTD9). */
export const unitStandardsKey = (standardIds: readonly string[]) => [...new Set(standardIds)].sort().join('|');

/**
 * The unit's topics from one hit list per standard, in the unit's standard order: published
 * only, ranked by standards matched, then best score, then first seen. Top five.
 */
export function rankUnitTopics(hitsPerStandard: readonly (readonly TopicCandidate[])[]): UnitTopic[] {
  const tally = new Map<string, { topic: TopicCandidate; matched: number; best: number; seen: number }>();
  for (const hits of hitsPerStandard) {
    const ids = new Set<string>();
    for (const hit of hits.filter(h => h.status === 'published')) {
      if (ids.size === HITS_PER_STANDARD) break;
      if (ids.has(hit.id)) continue;
      ids.add(hit.id);
      const entry = tally.get(hit.id);
      if (entry) { entry.matched += 1; entry.best = Math.max(entry.best, hit.score ?? 0); }
      else tally.set(hit.id, { topic: hit, matched: 1, best: hit.score ?? 0, seen: tally.size });
    }
  }
  return [...tally.values()]
    .sort((a, b) => b.matched - a.matched || b.best - a.best || a.seen - b.seen)
    .slice(0, TOPICS_PER_UNIT)
    .map(({ topic, matched }) => ({ id: topic.id, title: topicTitle(topic), matched }));
}

/** The catalog title a teacher reads, without the study app's subject prefix. Copied from One Step Ahead. */
export function topicTitle(topic: { name: string; displayName?: string }): string {
  const source = topic.displayName?.trim() || topic.name.trim();
  return source.replace(/^(?:M|S|ELA|SS)\.\s*(?=[A-Z][a-z])/, '') || source;
}
